// Canvas cannot read CSS custom properties, so the palette has to reach this module some other
// way. It is derived from ui/tokens.ts rather than retyped: the constraint is that the values
// arrive in two places, not that they are authored in two places. What lives here is the
// mapping from a colour to what it means on this canvas.

import { alpha, mix, onBlock, token } from "../ui/tokens";

export const theme = {
  // The well: the recessed tier of the surface ramp, below the page rather than above it.
  // Every surface is dark now, so this is a step down and not the last survivor of one.
  background: token.bgSunken,
  gridline: alpha(token.white, 0.06),
  // The loudness envelope, under everything. Faint on purpose: it is a backdrop that has to
  // stay legible behind the translucent block fills painted over it.
  waveform: alpha(token.white, 0.1),
  gridlineMajor: alpha(token.white, 0.12),

  showFill: alpha(token.accent, 0.18),
  showFillHover: alpha(token.accent, 0.3),
  showEdge: alpha(token.accent, 0.95),
  // Two tiers per look: the heading in the plain tint, the performer and the origin under it
  // in the dim one. See ui/tokens.onBlock for why they are a luminance step and not a hue.
  showText: onBlock.show,
  showTextDim: onBlock.showDim,

  approvedEdge: alpha(token.accent, 0.95),
  provisionalEdge: alpha(token.warn, 0.95),
  provisionalFill: alpha(token.warn, 0.14),
  provisionalText: onBlock.provisional,
  provisionalTextDim: onBlock.provisionalDim,

  // A show on air is provisional too, but it is not waiting for anybody -- it is waiting to
  // finish. It takes the playhead's colour rather than the amber of the review queue, so the
  // one block that is happening now reads as part of the live edge.
  liveEdge: alpha(token.danger, 0.95),
  liveFill: alpha(token.danger, 0.16),
  liveText: onBlock.live,
  liveTextDim: onBlock.liveDim,

  trackFill: alpha(token.jukebox, 0.2),
  trackFillHover: alpha(token.jukebox, 0.34),
  trackEdge: alpha(token.jukebox, 0.85),
  trackText: onBlock.track,
  trackTextDim: onBlock.trackDim,
  trackStarred: alpha(token.star, 0.95),

  tombstoneFill: alpha(token.muted, 0.13),
  tombstoneEdge: alpha(token.muted, 0.55),
  tombstoneText: onBlock.tombstone,
  tombstoneTextDim: onBlock.tombstoneDim,

  // A stretch inside the record with no playable audio: nothing was recorded, or what was has
  // aged out of the buffer with no export behind it. A flat wash and no hatch -- the stripes
  // moved to `voidHatch`, and over an expired show they read as a fault where there is none.
  // Grey rather than the pale warm this used to be, and `token.muted` rather than a new grey:
  // the palette already says "the archive knows, the audio is gone" in that colour under every
  // tombstone. A step up in alpha from `tombstoneFill`, because there is no block and no border
  // here -- the wash is the whole marking.
  gapFill: alpha(token.muted, 0.16),

  // Outside the record altogether: earlier than the first row the archive holds, and later
  // than the clock. The one marking stripes can make without ambiguity -- there is no audio
  // here because there is no *here* -- and the only hatch left on the canvas.
  voidHatch: alpha(token.gap, 0.3),

  // Audio the rolling buffer no longer holds but the permanent archive does. Amber rather
  // than the grey the gap wash and the tombstone share: nothing here is lost, and token.star
  // is already the palette's "kept past the buffer". Told apart from the gap by hue rather
  // than by texture now that neither is hatched, because the two make opposite claims -- a
  // gap has nothing to play, this has a show that plays from its own file. Only the bare
  // scrubber is refused across it.
  archivedFill: alpha(token.star, 0.07),
  archivedEdge: alpha(token.star, 0.4),

  // The listened tracker, in the padding directly under a show block. Grey for heard, and
  // `muted` rather than a new grey because the palette already says "nothing more to get
  // here" in that colour. Violet for unheard, which is the claim the tracker exists to make.
  //
  // Told apart by lightness and not by hue, which is what they used to lean on: both were
  // near-opaque and composited to 1.14:1 over the well, and a rule this thin is too thin for
  // the eye to read a hue off, so the two states were one grey-violet smear. Opaque violet
  // against a quarter-luminance grey is 2.97:1, and the loud one is the claim being made.
  // Heard still clears the well at 2.17:1, so a show played end to end reads as a grey rule
  // rather than as no rule at all.
  //
  // `mix` and not `alpha` for the grey, and that is the trap this fill sits in: it is painted
  // *over the violet base*, not over the well, so a wash here takes the violet's hue instead
  // of the well's black and lands back beside the colour it has to be told apart from. What
  // is wanted is the colour that wash would have made against the well, opaque.
  heardFill: mix(alpha(token.muted, 0.45), token.bgSunken),
  unheardFill: token.unheard,

  chatFill: alpha(token.chat, 0.55),
  chatFillQuiet: alpha(token.chat, 0.22),
  chatBaseline: alpha(token.white, 0.07),
  // Unlogged stretches. Grey rather than the audio gap's red: nothing is broken or missing
  // from the archive, we simply were not in the room, which is a normal state and is the
  // default one.
  chatUnlogged: alpha(token.muted, 0.14),

  playhead: token.danger,
  playheadGlow: alpha(token.danger, 0.25),

  // The stretch between the newest playable instant and the clock: recorded this second, not
  // served yet. It takes the playhead's hue because it is a fact about the live edge rather
  // than about the archive, and it is flat where the gap beside it is hatched, because the two
  // make opposite claims -- a gap is audio that was never recorded, this is audio arriving.
  liveDelayFill: alpha(token.danger, 0.12),
  liveDelayEdge: alpha(token.danger, 0.55),

  label: token.text,
  labelDim: token.textDim,
  selection: alpha(token.white, 0.85),
} as const;

export const layout = {
  rulerHeight: 46,
  /**
   * The show and track row.
   *
   * The height of what is actually drawn, and the canvas is sized from it rather than the
   * other way round: one constant per rectangle, and it is the one that reaches a pixel.
   */
  showHeight: 68,
  /**
   * The chat density lane, along the bottom.
   *
   * Its own row, and it has to be: shows and tracks share one lane precisely because they
   * never overlap in time, and chat overlaps all of it. Drawing it in the same band would
   * either hide blocks behind bars or hide bars behind blocks.
   *
   * Thin, because it carries one number per bucket. It is an at-a-glance answer to "was
   * anyone talking during this show" -- the words themselves are in the panel.
   */
  chatHeight: 24,
  /**
   * Breathing room between the row's blocks and the edges of the row.
   *
   * Small on purpose: blocks fill the row. A show and a jukebox track are the same kind of
   * thing at different scales, so they get the same height, and the row reads as one lane
   * rather than as a tall band with a strip stuck along the bottom of it.
   */
  rowPad: 4,
} as const;

/**
 * MP3 quantiser gain to a fraction of the band's half height.
 *
 * `global_gain` is logarithmic -- one step is about 1.5 dB -- so this is a linear-in-dB scale
 * with the bottom cut off. Measured over tests/data/icy_body.bin, the station's per-frame maxima
 * run 132..184 with the median at 174: 135 puts a fade-out on the floor and 188 leaves headroom
 * above the loudest thing observed. This is the calibration knob -- if the station's loudness
 * moves, these two numbers move, and nothing else does.
 */
export const waveGain = { floor: 135, ceil: 188 } as const;

/** 0 for silence or no recording, 1 at full scale. */
export function waveAmplitude(gain: number): number {
  if (gain <= waveGain.floor) return 0;
  return Math.min(1, (gain - waveGain.floor) / (waveGain.ceil - waveGain.floor));
}

export interface Bands {
  rulerTop: number;
  rulerBottom: number;
  showTop: number;
  showBottom: number;
  trackTop: number;
  trackBottom: number;
  chatTop: number;
  chatBottom: number;
  contentTop: number;
  contentBottom: number;
}

/**
 * The lane heights, at whichever of the two sizes is in force.
 *
 * Compact is for a phone held sideways: ~390px of screen for a header, a player, a list and
 * this, where the full-size canvas alone takes 146 of it and the list ends up with none. The
 * ruler loses its second line of date and the row loses the third caption line -- a block
 * still carries its title and its DJ, which is what the lane is read for.
 */
function lanes(compact: boolean) {
  return compact
    ? { ruler: 34, show: 48, chat: layout.chatHeight, pad: layout.rowPad }
    : {
        ruler: layout.rulerHeight,
        show: layout.showHeight,
        chat: layout.chatHeight,
        pad: layout.rowPad,
      };
}

/**
 * How tall the canvas has to be to hold the lanes that exist.
 *
 * A function of the data rather than a constant, because a lane nobody logged for is not a
 * thin lane, it is no lane: drawn unconditionally it fills the width with one flat grey
 * rectangle that looks like padding, swallows the pointer, and says nothing. The chat lane is
 * the only optional one so far, and the shape here is what lets the next one be optional too.
 */
export function timelineHeight(chat: boolean, compact = false): number {
  const l = lanes(compact);
  const row = l.ruler + l.pad + l.show + l.pad;
  return chat ? row + l.chat : row;
}

export function bands({ chat, compact = false }: { chat: boolean; compact?: boolean }): Bands {
  // Shows and tracks occupy one row and both fill it. The shared band is what makes a
  // fingerprint-matched track read as a layer over the show it was heard inside: the track
  // lane is drawn second, so its wash lands on the show's own block rather than beside it.
  // A jukebox track never shares its span with a show, so nothing else is hidden either.
  const l = lanes(compact);
  const height = timelineHeight(chat, compact);
  const rulerTop = 0;
  const rulerBottom = l.ruler;
  const showTop = rulerBottom + l.pad;
  const showBottom = showTop + l.show;
  const trackTop = showTop;
  const trackBottom = showBottom;
  // Collapsed to nothing rather than to something short: `drawChatDensity` returns on a lane
  // of no height, so switching the setting off takes the baseline and the unlogged shading
  // with it instead of leaving a thinner version of the same grey bar.
  const chatTop = chat ? height - l.chat : height;
  const chatBottom = height;
  return {
    rulerTop,
    rulerBottom,
    showTop,
    showBottom,
    trackTop,
    trackBottom,
    chatTop,
    chatBottom,
    // Gaps and hit-testing take the segment area below the ruler, padding included: a gap is
    // a property of the window rather than of a block, and four pixels of dead zone above a
    // block is four pixels of clicks that go nowhere. The chat lane is excluded -- a click
    // there is not a click on a segment, and treating it as one would select a block the
    // pointer is nowhere near. With no chat lane there is nothing to exclude, and the pointer
    // works everywhere the canvas is visible.
    contentTop: rulerBottom,
    contentBottom: chatTop,
  };
}
